import {HttpHeaders} from '@angular/common/http';

export class Helper {

  private static sequenceNumber = 1;

  /**
   * Generates new sequence number.
   */
  public static nextSequenceNumber(): number {
    Helper.sequenceNumber += 1;

    return Helper.sequenceNumber;
  }

  /**
   * Shallow clone the obj.
   * @param obj
   */
  public static clone(obj: any): any {
    if(obj === null) {
      return null;
    }
    else if(obj === undefined) {
      return undefined;
    }
    else {
      return Object.assign({}, obj);
    }
  }

  public static isNullOrUndefined(obj: any): boolean {
    return obj === null || obj === undefined;
  }

  public static isValid(obj: any): boolean {
    return !Helper.isNullOrUndefined(obj);
  }

  public static isNonEmptyArray(array: Array<any>|null|undefined): boolean {
    if(Helper.isValid(array)) {
      if(array!.length !== 0) {
        return true;
      }
    }

    return false;
  }

  public static arraysEqual(arr1: any, arr2: any) {
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = arr1.length; i--;) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }

  public static arrayCopy(array: Array<any>): Array<any> {
    const results: any[] = [];
    array.forEach((e) => results.push(Object.assign({}, e)));

    return results;
  }

  public static objsEqual(obj1: any, obj2: any) {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  /*
     Regex to match and filter the contents inside the script tags..
  */
  public static removeScriptTagIfPresent(msg: any) {
    if (msg) {
      if (/<[\s]*script\b[^>]*>([\s\S]*?)<\/[\s]*script\b[^\/]*>/mgi.test(msg)) {
        msg = msg.replace(/<[\s]*script\b[^>]*>([\s\S]*?)<\/[\s]*script\b[^\/]*>/mgi, '');
      } else if (/<[\s]*script\b[^>]*>([\s\S]*?)/gmi.test(msg)) {
        return msg.replace(/<[\s]*script\b[^>]*>([\s\S]*?)/gmi, '');
      }
      return msg;
    }
  }

  /*
      Returns the body of the request for checking the validity of the email parameter
  */
  public static checkEmailJsonBody(email: string) {
    return JSON.parse(`
            {
                "email":[
                    {"value":"${email}"}
                ]
            }`);
  }

  /*
      Returns a request header for basic auth. The value is being created from the parameters
  */
  public static authHeaderFromUserAndPassword(username: string, password: string) {
    let authBase64 = btoa(username + ':' + password);
    let headers = new HttpHeaders({'Authorization': `Basic ${authBase64}`});
    return {headers: headers};
  }

  public static authHeaderFromRefreshToken() {
    let currentUser = JSON.parse(localStorage.getItem('impersonatedUser') ?? '') ?
      JSON.parse(localStorage.getItem('impersonatedUser') ?? '') : JSON.parse(localStorage.getItem('currentUser') ?? '');
    let headers;

    if (currentUser && currentUser.refreshToken) {
      headers = new HttpHeaders({'Authorization': `Refresh ${currentUser.refreshToken}`});
      // headers.set('Authorization', `Refresh ${currentUser.refreshToken}`)
    } else {
      headers = new HttpHeaders({'Authorization': '__invalid__'});
    }
    return {headers: headers};
  }

  public static decodeHtml(htmlEncodedText: any) {
    if (!htmlEncodedText) {
      return htmlEncodedText;
    }

    let ta = document.createElement('textarea');
    ta.innerHTML = Helper.removeScriptTagIfPresent(htmlEncodedText);
    return ta.value;
  }

  public static mergeJsonObjs(source: any, target: any) {
    if (!source) {
      return;
    }
    if (!target) {
      return;
    }

    for (let sourceProp in source) {
      target[sourceProp] = source[sourceProp];
    }
  }

  public static jsonStringToObj(jsonString: string) {
    jsonString = Helper.decodeHtml(jsonString);
    if (!jsonString || jsonString.length === 0) {
      return {};
    }
    return JSON.parse(jsonString);
  }

  public static getTranslationFromJsonString(jsonString: string, langCode: string, defaultValue: string | null = null) {
    if (!Helper.checkJsonString(jsonString)) {
      return jsonString;
    }

    let translationObj = Helper.jsonStringToObj(jsonString);

    return Helper.getTranslationFromJson(translationObj, langCode, defaultValue);
  }

  public static checkJsonString(json: any) {
    try {
      JSON.parse(json);
    } catch (e) {
      return false;
    }
    return true;
  }

  public static getTranslationFromJson(json: any, langCode: string | undefined, defaultValue: string | null = null) {
    try {
      if (!langCode || langCode.trim().length === 0) {
        langCode = 'Default';
      } else if (langCode === 'en') {
        langCode = 'Default';
      }

      let result: string = '';

      if (json.hasOwnProperty(langCode)) {
        result = json[langCode];
      } else {
        result = json['en'];
      }

      if (result && result.length > 0) {
        return result;
      } else {
        return defaultValue;
      }
    } catch (e) {
      return defaultValue;
    }
  }

  public static buildTwErrorObject(error: any) {
    try {
      return {
        twMessage: {
          status: error.status,
          message: error.text(),
        }
      };
    } catch (e) {
      return {
        twMessage: {
          status: 500,
          message: 'Server error',
        }
      }
    }
  }

  /**
   * Concatenates 2 object string properties.
   * @param obj1
   * @param obj2
   * @param ignoreKeys
   * @param joinBy
   */
  public static concatObjectStringProperties(obj1: any, obj2: any, ignoreKeys: Array<string> = [], joinBy = '') {
    let result = Object.assign({}, obj1);
    for (let prop in obj1) {
      let joinS = joinBy;
      let keyIsIgnored = ignoreKeys.find(it => it === prop) !== undefined;
      if (obj1.hasOwnProperty(prop) && typeof obj1[prop] === 'string'
        && obj2.hasOwnProperty(prop) && !keyIsIgnored) {
        // Override joiner string when second object is empty.
        if (String(obj1[prop]).length === 0 || String(obj2[prop]).length === 0) {
          joinS = '';
        }
        result[prop] = result[prop] + joinS + obj2[prop];
      }
    }
    return result;
  }

  /**
   * Recursive object copying. Add more logic in here if needed. Stops at 10 recursions.
   *
   * @param base
   * @param toCopy
   *
   * @param {number} level
   * @returns {any}
   */
  public static recursiveObjAssign(base: any, toCopy: any, level = 0) {
    if (level === 10) {
      return;
    }
    for (let prop in toCopy) {
      if (base.hasOwnProperty(prop)) {
        if (typeof toCopy[prop] === 'object') {
          base[prop] = Helper.recursiveObjAssign(base[prop], toCopy[prop], level + 1);
        } else {
          base[prop] = toCopy[prop];
        }
      } else {
        base[prop] = toCopy[prop];
      }
    }
    return base;
  }

  public static arrayToString(array: Array<any>, delimiter = ', '): string {
    let result = '';

    if (!array || array.length === 0) {
      return result;
    }

    array.forEach((element) => {
      if (result.length > 0) {
        result += delimiter;
      }

      result += element.toString();
    });

    return result;
  }

  public static stringToArray(str: string, delimiter = ', '): Array<string> {
    if (!str || str.length === 0) {
      return [];
    }

    return str.split(delimiter);
  }

  public static isLocal(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }
}
